import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, FileText, Download, Share2, ArrowLeft, Loader2, LogIn } from "lucide-react";
import logo from "@/assets/dosatronic-logo.png";

const API = process.env.REACT_APP_BACKEND_URL;
const ytEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

export default function PublicProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(user ? "/" : "/login");
  };
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [activeVid, setActiveVid] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/public/products/${id}?lang=${lang}`)
      .then(({ data }) => { setProduct(data); setActive(0); setActiveVid(0); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id, lang]);

  const share = async () => {
    const url = `${window.location.origin}/p/${id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: product?.name, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t("products.shareCopied"));
      }
    } catch { /* user cancelled */ }
  };
  const waShare = () => {
    const url = `${window.location.origin}/p/${id}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(`${product?.name} — ${url}`)}`, "_blank");
  };

  const imgUrl = (iid) => `${API}/api/public/products/${id}/image/${iid}`;
  const vidLabel = { montaje: t("adminProducts.vidMontaje"), mantenimiento: t("adminProducts.vidMantenimiento"), demo: t("adminProducts.vidDemo"), otro: t("adminProducts.vidOtro") };

  return (
    <div className="min-h-screen bg-white">
      <Seo title={product ? `${product.name} — Dosacloud | Dosatronic` : "Dosatronic"} description={product?.description || t("products.publicSubtitle")} path={`/p/${id}`} />

      <header className="flex items-center justify-between border-b border-slate-100 px-6 py-4 sm:px-10">
        <Link to={user ? "/" : "/login"}><img src={logo} alt="Dosatronic" className="h-8 w-auto" /></Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <Button variant="ghost" size="sm" onClick={goBack} data-testid="public-back"><ArrowLeft className="mr-1.5 h-4 w-4" /> {t("about.backHome")}</Button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-6 py-10 sm:px-10">
        {loading && <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
        {notFound && <p className="py-20 text-center text-slate-400" data-testid="public-notfound">404 · {t("products.noResults")}</p>}

        {product && (
          <div data-testid="public-product">
            <nav className="mb-2 flex items-center gap-1 text-xs text-slate-400" data-testid="public-breadcrumb">
              <Link to="/productos" className="hover:text-blue-600">{t("products.home")}</Link>
              <span>›</span>
              <Link to={`/productos?category=${product.group}`} className="hover:text-blue-600">{t(`products.group.${product.group}`)}</Link>
              <span>›</span>
              <span className="font-medium text-slate-600">{product.name}</span>
            </nav>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">{t(`products.group.${product.group}`)}</p>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900">{product.name}</h1>
              {product.is_new && <Badge className="bg-emerald-500 text-white hover:bg-emerald-500">{t("products.badgeNew")}</Badge>}
              {product.featured && <Badge className="bg-blue-600 text-white hover:bg-blue-600">{t("products.badgeFeatured")}</Badge>}
            </div>
            {product.reference && <p className="mt-1 text-sm text-slate-500" data-testid="public-reference">{t("products.reference")}: <span className="font-medium">{product.reference}</span></p>}

            <div className="mt-5 flex flex-wrap gap-2">
              <Button onClick={share} data-testid="public-share" className="bg-blue-600 text-white hover:bg-blue-700"><Share2 className="mr-1.5 h-4 w-4" /> {t("products.share")}</Button>
              <Button onClick={waShare} variant="outline" data-testid="public-share-wa"><svg viewBox="0 0 24 24" className="mr-1.5 h-4 w-4 fill-current"><path d="M17.5 14.4c-.3-.1-1.7-.8-1.9-.9-.3-.1-.4-.1-.6.1-.2.3-.7.9-.8 1-.2.2-.3.2-.6.1-1.5-.7-2.5-1.3-3.5-3-.3-.5.3-.4.8-1.4.1-.2 0-.4 0-.5C10.3 9.3 9.9 8 9.7 7.5c-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.3.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2 3.1 5 4.3 1.8.8 2.5.8 3.4.7.5-.1 1.7-.7 2-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3z"/></svg> WhatsApp</Button>
              <Button asChild variant="outline" data-testid="public-viewapp"><Link to="/productos"><LogIn className="mr-1.5 h-4 w-4" /> {t("products.viewInApp")}</Link></Button>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
              <div>
                <div className="flex h-64 items-center justify-center rounded-lg bg-white">
                  {product.images?.length > 0 ? (
                    <img src={imgUrl(product.images[active].id)} alt={product.name} className="h-full w-full object-contain p-4" data-testid="public-image" />
                  ) : <Package className="h-14 w-14 text-slate-300" />}
                </div>
                {product.images?.length > 1 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {product.images.map((im, i) => (
                      <button key={im.id} onClick={() => setActive(i)}
                        className={`h-14 w-14 overflow-hidden rounded border ${i === active ? "border-blue-500 ring-2 ring-blue-200" : "border-slate-200"}`}>
                        <img src={imgUrl(im.id)} alt="" className="h-full w-full object-contain" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm leading-relaxed text-slate-600">{product.description}</p>
                {product.specs?.length > 0 && (
                  <table className="mt-4 w-full text-sm" data-testid="public-specs">
                    <tbody>
                      {product.specs.map((s, i) => (
                        <tr key={i} className="border-b border-slate-100 last:border-0">
                          <td className="py-1.5 pr-3 text-slate-500">{s.label}</td>
                          <td className="py-1.5 text-right font-medium text-slate-800">{s.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {(() => {
              const videos = (product.videos || []).map((v) => ({ ...v, embed: ytEmbed(v.url) })).filter((v) => v.embed);
              if (videos.length === 0) return null;
              return (
                <div className="mt-8" data-testid="public-video">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t("products.video")}</h3>
                  {videos.length > 1 && (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {videos.map((v, i) => (
                        <button key={i} onClick={() => setActiveVid(i)}
                          className={`rounded-full border px-3 py-1 text-xs font-medium ${i === activeVid ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500"}`}>
                          {vidLabel[v.label] || v.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="aspect-video w-full overflow-hidden rounded-lg border border-slate-200">
                    <iframe src={videos[activeVid].embed} title={product.name} className="h-full w-full" allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
                  </div>
                </div>
              );
            })()}

            {product.manuals?.length > 0 && (
              <Card className="mt-8 border-slate-200 p-5 shadow-none">
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{t("products.manualsTitle")}</h3>
                <p className="mb-3 text-xs text-amber-600">{t("products.loginToDownload")}</p>
                <div className="space-y-1.5">
                  {product.manuals.map((m) => (
                    <button key={m.id} onClick={() => navigate("/login")} data-testid={`public-manual-${m.id}`}
                      className="flex w-full items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-left text-sm text-slate-600 transition-colors hover:border-blue-300 hover:bg-blue-50">
                      <FileText className="h-4 w-4 shrink-0 text-blue-600" />
                      <span className="flex-1 truncate">{m.title}</span>
                      <Download className="h-4 w-4 text-slate-400" />
                    </button>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
